import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable } from 'rxjs';

export type Theme = 'light' | 'dark';

@Injectable({
	providedIn: 'root',
})
export class ThemeService {
	private readonly THEME_KEY = 'selected-theme';
	private currentThemeSubject: BehaviorSubject<Theme>;
	public readonly theme$: Observable<Theme>;

	constructor(@Inject(PLATFORM_ID) private platformId: any) {
		// Default to light theme
		const savedTheme = this.getSavedTheme();
		this.currentThemeSubject = new BehaviorSubject<Theme>(savedTheme);
		this.theme$ = this.currentThemeSubject.asObservable();

		// Apply theme on initialization
		this.applyTheme(savedTheme);
	}

	/**
	 * Get the currently active theme
	 */
	public getCurrentTheme(): Theme {
		return this.currentThemeSubject.value;
	}

	/**
	 * Set a specific theme
	 */
	public setTheme(theme: Theme): void {
		this.saveTheme(theme);
		this.currentThemeSubject.next(theme);
		this.applyTheme(theme);
	}

	/**
	 * Toggle between light and dark themes
	 */
	public toggleTheme(): void {
		const newTheme = this.getCurrentTheme() === 'light' ? 'dark' : 'light';
		this.setTheme(newTheme);
	}

	/**
	 * Check if dark theme is active
	 */
	public isDarkTheme(): boolean {
		return this.getCurrentTheme() === 'dark';
	}

	/**
	 * Save theme preference to localStorage
	 */
	private saveTheme(theme: Theme): void {
		if (isPlatformBrowser(this.platformId)) {
			localStorage.setItem(this.THEME_KEY, theme);
		}
	}

	/**
	 * Get saved theme from localStorage
	 */
	private getSavedTheme(): Theme {
		if (isPlatformBrowser(this.platformId)) {
			const savedTheme = localStorage.getItem(this.THEME_KEY) as Theme;
			return savedTheme || this.getPreferredTheme();
		}
		return 'light'; // Default for server-side rendering
	}

	/**
	 * Get user's preferred theme from system settings
	 */
	private getPreferredTheme(): Theme {
		if (isPlatformBrowser(this.platformId)) {
			return window?.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
		}
		return 'light';
	}

	/**
	 * Apply the theme to the document
	 */
	private applyTheme(theme: Theme): void {
		if (isPlatformBrowser(this.platformId)) {
			document.documentElement.classList.remove('light', 'dark');
			document.documentElement.classList.add(`${theme}`);
		}
	}
}
